import { usePageMeta } from "@/lib/seo";
import { Link, Navigate, useLocation } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { EmailAuthForm } from "@/components/EmailAuthForm";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/lib/config";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export default function Login() {
  usePageMeta({ title: "Sign in", description: "Sign in to RizzAura with Google or email and try 3 AI rizz replies free.", index: true });
  const { user, loading, login, onboarding, config } = useAuth();
  const location = useLocation();

  if (!loading && user && onboarding) return <Navigate to={onboarding.complete ? "/app" : "/onboarding"} replace />;
  const next = location.state?.from && location.state.from !== "/login" ? location.state.from : "/app";

  return (
    <div className="min-h-screen grid-bg flex flex-col" data-testid="login-page">
      <div className="flex justify-end p-4"><ThemeToggle /></div>
      <main className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="panel w-full max-w-md p-8 sm:p-10 text-center rise">
          <div className="mx-auto w-fit mb-6"><LogoMark size={64} /></div>
          <div className="font-display font-extrabold text-sm tracking-tight text-muted-foreground mb-2">{APP_NAME}</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Welcome to {APP_NAME}</h1>
          <p className="text-muted-foreground mt-3">Get smarter, more natural replies for your conversations.</p>

          {loading || !config ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto mt-8" />
          ) : config.auth_configured ? (
            <button data-testid="login-google-button" onClick={() => login(next)}
              className="mt-8 w-full h-13 py-3.5 rounded-full bg-foreground text-background font-semibold inline-flex items-center justify-center gap-3 hover:opacity-90 transition-opacity">
              <GoogleIcon /> Continue with Google
            </button>
          ) : null}
          {config?.auth_configured && (
            <>
              <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground"><span className="flex-1 h-px bg-border" />or use email<span className="flex-1 h-px bg-border" /></div>
              <EmailAuthForm />
            </>
          )}
          {config && !loading && !config.auth_configured && (
            <div data-testid="login-setup-message" className="mt-8 flex gap-2 text-left rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{config.unreachable ? "We can't reach the server right now. Please retry in a moment." : "Google sign-in isn't configured on this server yet. Add the Supabase project settings (SUPABASE_URL, SUPABASE_ANON_KEY, SUPABASE_SERVICE_ROLE_KEY, DATABASE_URL) to the backend environment to enable it."}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">With Google we only receive your name and email — never your Google password, Gmail, Drive or Contacts. Email passwords are stored securely hashed. 18+ only.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs">
            <Link to="/legal/privacy" data-testid="login-privacy-link" className="text-primary hover:underline">Privacy Policy</Link>
            <Link to="/legal/terms" data-testid="login-terms-link" className="text-primary hover:underline">Terms of Service</Link>
            <Link to="/legal/community" data-testid="login-community-link" className="text-primary hover:underline">Community Guidelines</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
